import type { ClubPrintPaletteId } from "@mixitone/models";

export type Rgb = readonly [number, number, number];

export type PrintPaletteColors = {
  cyan: Rgb;
  cyanDark: Rgb;
  cyanDeep: Rgb;
  cyanMuted: Rgb;
  cyanWash: Rgb;
  slate900: Rgb;
  slate800: Rgb;
  slate700: Rgb;
  slate500: Rgb;
  slate200: Rgb;
  mixGray: Rgb;
  white: Rgb;
  onAccent: Rgb;
};

export type PrintPalette = {
  id: ClubPrintPaletteId;
  label: string;
  colors: PrintPaletteColors;
};

export const DEFAULT_PRINT_PALETTE: ClubPrintPaletteId = "teal";

const TEAL: PrintPaletteColors = {
  cyan: [8, 145, 178],
  cyanDark: [14, 116, 144],
  cyanDeep: [7, 89, 110],
  cyanMuted: [165, 233, 244],
  cyanWash: [236, 254, 255],
  slate900: [15, 23, 42],
  slate800: [30, 41, 59],
  slate700: [51, 65, 85],
  slate500: [100, 116, 139],
  slate200: [226, 232, 240],
  mixGray: [107, 114, 128],
  white: [255, 255, 255],
  onAccent: [255, 255, 255],
};

export const PRINT_PALETTES: PrintPalette[] = [
  {
    id: "teal",
    label: "Teal",
    colors: TEAL,
  },
  {
    id: "slateYellow",
    label: "Slate & yellow",
    colors: {
      cyan: [234, 179, 8],
      cyanDark: [202, 138, 4],
      cyanDeep: [161, 98, 7],
      cyanMuted: [253, 224, 71],
      cyanWash: [254, 252, 232],
      slate900: [51, 65, 85],
      slate800: [71, 85, 105],
      slate700: [71, 85, 105],
      slate500: [100, 116, 139],
      slate200: [226, 232, 240],
      mixGray: [107, 114, 128],
      white: [255, 255, 255],
      onAccent: [51, 65, 85],
    },
  },
  {
    id: "blackOrange",
    label: "Black & orange",
    colors: {
      cyan: [234, 88, 12],
      cyanDark: [194, 65, 12],
      cyanDeep: [154, 52, 18],
      cyanMuted: [253, 186, 116],
      cyanWash: [255, 247, 237],
      slate900: [0, 0, 0],
      slate800: [24, 24, 27],
      slate700: [63, 63, 70],
      slate500: [113, 113, 122],
      slate200: [228, 228, 231],
      mixGray: [107, 114, 128],
      white: [255, 255, 255],
      onAccent: [255, 255, 255],
    },
  },
  {
    id: "navySky",
    label: "Navy & sky",
    colors: {
      cyan: [14, 165, 233],
      cyanDark: [2, 132, 199],
      cyanDeep: [3, 105, 161],
      cyanMuted: [125, 211, 252],
      cyanWash: [240, 249, 255],
      slate900: [15, 23, 42],
      slate800: [30, 41, 59],
      slate700: [51, 65, 85],
      slate500: [100, 116, 139],
      slate200: [226, 232, 240],
      mixGray: [107, 114, 128],
      white: [255, 255, 255],
      onAccent: [255, 255, 255],
    },
  },
  {
    id: "forest",
    label: "Forest",
    colors: {
      cyan: [22, 163, 74],
      cyanDark: [21, 128, 61],
      cyanDeep: [20, 83, 45],
      cyanMuted: [134, 239, 172],
      cyanWash: [240, 253, 244],
      slate900: [6, 78, 59],
      slate800: [6, 95, 70],
      slate700: [52, 83, 66],
      slate500: [87, 124, 102],
      slate200: [209, 232, 216],
      mixGray: [107, 114, 128],
      white: [255, 255, 255],
      onAccent: [255, 255, 255],
    },
  },
];

const PALETTE_BY_ID = new Map(PRINT_PALETTES.map((palette) => [palette.id, palette]));

export function isPrintPaletteId(value: string | null | undefined): value is ClubPrintPaletteId {
  return value != null && PALETTE_BY_ID.has(value as ClubPrintPaletteId);
}

export function resolvePrintPaletteId(value: string | null | undefined): ClubPrintPaletteId {
  return isPrintPaletteId(value) ? value : DEFAULT_PRINT_PALETTE;
}

export function paletteColors(value: string | null | undefined): PrintPaletteColors {
  return PALETTE_BY_ID.get(resolvePrintPaletteId(value))?.colors ?? TEAL;
}

export function rgbToHex(rgb: Rgb): string {
  return `#${rgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

export function paletteSwatch(palette: PrintPalette): { ink: string; accent: string } {
  return {
    ink: rgbToHex(palette.colors.slate900),
    accent: rgbToHex(palette.colors.cyan),
  };
}
